/**
 * ReportsSidebar Component
 *
 * A collapsible left sidebar for the Reports section. It lists a fixed set of
 * options: All Reports (the default), Recently Added, Needs Review (reports with
 * no report type), and one entry per report type under a "Report Types"
 * heading. Exactly one option is highlighted at a time.
 *
 * Mirrors the layout and button styling of JournalArticlesSidebar, but has no
 * collections, topics, dialogs, or admin actions. Uses the generic
 * SecondarySideBar for the open/close toggle, layout, and collapsed icon rail.
 */

import {
  Activity,
  BarChart3,
  Building2,
  ClipboardList,
  FileClock,
  FileQuestion,
  Files,
  Globe,
  Microscope,
  Tag,
  Users,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import SecondarySideBar from "@/components/shared/SecondarySideBar"
import {
  NEEDS_REVIEW_LABEL,
  REPORT_TYPES,
  ReportType,
  reportTypeLabelMap,
} from "@/services/reportsService"
import { ReportsSidebarOption } from "@/components/reports/reportsRoutes"

/** Title shown in the sidebar header and used in the collapsed "Expand" button label. */
const SIDEBAR_TITLE = "Reports"

/** Display label and icon for one sidebar option. */
interface OptionMeta {
  label: string
  icon: React.ReactNode
}

/**
 * Icon for each report type. Typed as a full Record so adding a value to
 * REPORT_TYPES without an icon here is a compile error.
 */
const REPORT_TYPE_ICONS: Record<ReportType, React.ReactNode> = {
  globalReport: <Globe className="size-4" />,
  plan: <ClipboardList className="size-4" />,
  householdSurvey: <Users className="size-4" />,
  surveillance: <Activity className="size-4" />,
  healthStatistics: <BarChart3 className="size-4" />,
  institutionalReport: <Building2 className="size-4" />,
  research: <Microscope className="size-4" />,
}

/** The options listed above the "Report Types" heading, in display order. */
const TOP_OPTIONS: ReadonlyArray<[ReportsSidebarOption, OptionMeta]> = [
  ["all-reports", { label: "All Reports", icon: <Files className="size-4" /> }],
  ["recently-added", { label: "Recently Added", icon: <FileClock className="size-4" /> }],
  // Reports with no reportType: not a report type, so listed with the views.
  ["needs-review", { label: NEEDS_REVIEW_LABEL, icon: <FileQuestion className="size-4" /> }],
]

/**
 * One option per report type, in sidebar order (the order of REPORT_TYPES),
 * labelled from reportTypeLabelMap.
 */
const REPORT_TYPE_OPTIONS: ReadonlyArray<[ReportsSidebarOption, OptionMeta]> =
  REPORT_TYPES.map(
    (value: ReportType): [ReportsSidebarOption, OptionMeta] => [
      `type-${value}`,
      { label: reportTypeLabelMap[value], icon: REPORT_TYPE_ICONS[value] },
    ],
  )

/** Props for the ReportsSidebar component. */
interface ReportsSidebarProps {
  /** The currently active sidebar option (highlighted). */
  selectedOption: ReportsSidebarOption
  /** Called with the option the user clicked. */
  onOptionChange: (option: ReportsSidebarOption) => void
  /** Controlled mode: whether the sidebar is expanded. */
  isOpen?: boolean
  /** Controlled mode: callback to toggle the sidebar's expanded state. */
  onToggle?: () => void
}

/**
 * Collapsible sidebar for the Reports section.
 *
 * @param props - Component props.
 * @param props.selectedOption - The currently active sidebar option.
 * @param props.onOptionChange - Handler called when an option is selected.
 * @param props.isOpen - Optional controlled expanded state (used by the detail page).
 * @param props.onToggle - Optional controlled toggle handler (used with isOpen).
 * @returns The rendered sidebar element.
 */
export default function ReportsSidebar({
  selectedOption,
  onOptionChange,
  isOpen,
  onToggle,
}: ReportsSidebarProps): React.JSX.Element {
  /**
   * Renders one option as a full-width button in the expanded sidebar, using
   * the same classes as JournalArticlesSidebar's buttons.
   *
   * @param option - The sidebar option identifier.
   * @param meta - The option's label and icon.
   * @returns The rendered button.
   */
  const renderSidebarButton = (
    option: ReportsSidebarOption,
    meta: OptionMeta,
  ): React.JSX.Element => {
    const isSelected: boolean = option === selectedOption
    return (
      <button
        key={option}
        type="button"
        onClick={() => onOptionChange(option)}
        aria-label={meta.label}
        // aria-current tells screen readers which option is the active view.
        aria-current={isSelected ? "page" : undefined}
        className={cn(
          "text-left text-xs px-2 py-1 rounded-md transition-colors w-full",
          isSelected ? "bg-accent text-accent-foreground font-medium" : "hover:bg-muted",
        )}
      >
        <span className="flex items-center gap-2">
          {meta.icon}
          {meta.label}
        </span>
      </button>
    )
  }

  /**
   * Renders one option as an icon-only button with a tooltip for the collapsed
   * rail, using the same classes as JournalArticlesSidebar's collapsed buttons.
   *
   * @param option - The sidebar option identifier.
   * @param meta - The option's label (tooltip and accessible name) and icon.
   * @returns The rendered icon button wrapped in a tooltip.
   */
  const renderCollapsedButton = (
    option: ReportsSidebarOption,
    meta: OptionMeta,
  ): React.JSX.Element => {
    const isSelected: boolean = option === selectedOption
    return (
      <Tooltip key={option}>
        <TooltipTrigger asChild>
          <span className="w-full flex justify-center">
            <button
              type="button"
              onClick={() => onOptionChange(option)}
              aria-label={meta.label}
              aria-current={isSelected ? "page" : undefined}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded transition-colors",
                isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted cursor-pointer",
              )}
            >
              {meta.icon}
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">{meta.label}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <SecondarySideBar
      isOpen={isOpen}
      onToggle={onToggle}
      sidebarTitle={SIDEBAR_TITLE}
      collapsedContent={
        <>
          {TOP_OPTIONS.map(([option, meta]) => renderCollapsedButton(option, meta))}
          {/* Divider separates the report types from the top options in the rail. */}
          <hr className="w-6 border-border" aria-hidden="true" />
          {REPORT_TYPE_OPTIONS.map(([option, meta]) => renderCollapsedButton(option, meta))}
        </>
      }
    >
      <div className="flex flex-col gap-0.5">
        {TOP_OPTIONS.map(([option, meta]) => renderSidebarButton(option, meta))}
      </div>

      {/* Report Types group. A static heading (not collapsible): the list is
          short and fixed. Styled like the Topics heading in JournalArticlesSidebar. */}
      <section aria-labelledby="reports-sidebar-report-types-heading">
        <h3
          id="reports-sidebar-report-types-heading"
          className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold text-muted-foreground"
        >
          <Tag className="size-4" aria-hidden="true" />
          Report Types
        </h3>
        <div className="flex flex-col gap-0.5 py-1 pl-3">
          {REPORT_TYPE_OPTIONS.map(([option, meta]) => renderSidebarButton(option, meta))}
        </div>
      </section>
    </SecondarySideBar>
  )
}
